import React, { useCallback, useMemo } from 'react';
import { ContextMenu } from '../../../shared/ui/table';
import type { QuickFillItem } from '../../../shared/types';
import { ModelStatusBanner, QuickFillUtilityChrome } from './components';
import type { QuickFillPropertyPanelState } from './components';
import { buildQuickFillGuideContent } from './quickFillGuideContent';
import {
  buildQuickFillActionBarProps,
  buildQuickFillDialogShellProps,
  buildQuickFillNavigationDockProps,
  buildQuickFillSidePanelsProps,
  buildQuickFillTableProps,
  buildQuickFillWorkspaceProps,
} from '../lib';
// Pure helper imported directly — it must not travel through the controller
// god object just to reach this composition layer.
import { isQuickFillDatasetModeReady } from '../lib/quickFillPageModel';
import type { QuickFillPageValidationController } from '../hooks/useQuickFillPageValidationController';

export function useQuickFillPagePropComposition(
  controller: QuickFillPageValidationController,
  onContinue: () => void
) {
  const c = controller;
  const selectedRows = c.operations.selectedRows;
  const selectedCells = c.cellSelection.selectedCells;
  const activeColumn = c.cellSelection.activeColumn;
  const fillDownCellSelectionCount = c.cellFillDownPlan?.selectionCount ?? 0;
  // Methods invoked inside the memoized groups below are destructured into
  // locals first: exhaustive-deps treats `c.tour.x()` as a call on receiver
  // `c.tour` and would force the wrapper objects (rebuilt every render) into
  // the dependency arrays, defeating the memos.
  const {
    handleAutoFillPreviewDialog,
    setFindReplaceOpen,
    setCSVImportOpen,
  } = c.tour;
  const {
    handleUndoQuickFillSessions,
    handleUndoParameterSetup,
    getOptionsForProperty,
    getColumnUsageStats,
    updateExtraPropertyChecked,
    updatePropertyChecked,
    addExtraProperty,
  } = c;
  const setPropertyPanelOpen = c.setPropertyPanelOpen;
  const quickFillGuideContent = useMemo(() => {
    const content = buildQuickFillGuideContent({
      visibleColumnCount: c.visibleProperties.length,
      rowCount: c.quickFillData.length,
      workflowMode: c.configSync.workflowMode,
      isTableLoading: c.isTableLoading,
    });
    const resumeIndex = c.tour.tourResumeStep ?? -1;
    const resumeStep = c.quickFillTourSteps[resumeIndex] ?? null;
    return resumeStep ? {
      ...content,
      status: `Tour paused at ${resumeIndex + 1}/${c.quickFillTourSteps.length}: ${resumeStep.title}`,
      actionLabel: 'Resume tour',
      secondaryActionLabel: 'Restart tour',
      onSecondaryAction: c.tour.handleRestartTour,
    } : content;
  }, [
    c.configSync.workflowMode, c.isTableLoading, c.quickFillData.length,
    c.quickFillTourSteps, c.tour.handleRestartTour, c.tour.tourResumeStep,
    c.visibleProperties.length,
  ]);
  // Scroll-driven state (c.navigation.viewportRange) re-renders this hook on
  // every viewport tick, so each prop group is memoized on exactly the leaf
  // values it consumes and every inline closure handed to a builder is a
  // stable useCallback — otherwise memoized consumers (ActionBar, panels)
  // re-render on every scroll frame.
  const handleAutoFillAll = useCallback(
    () => handleAutoFillPreviewDialog('all'),
    [handleAutoFillPreviewDialog]
  );
  const handleAutoFillSelected = useCallback(
    () => handleAutoFillPreviewDialog('selected'),
    [handleAutoFillPreviewDialog]
  );
  const handleFindReplaceOpen = useCallback(
    () => setFindReplaceOpen(true),
    [setFindReplaceOpen]
  );
  const handleCSVImportOpen = useCallback(
    () => setCSVImportOpen(true),
    [setCSVImportOpen]
  );
  const handleTogglePropertyPanel = useCallback(
    () => setPropertyPanelOpen(!c.propertyPanelOpen),
    [c.propertyPanelOpen, setPropertyPanelOpen]
  );
  const handlePropertyCheckedChange: QuickFillPropertyPanelState['onPropertyCheckedChange'] =
    useCallback(
      (id, checked) => id.startsWith('extra-')
        ? updateExtraPropertyChecked(id, checked)
        : updatePropertyChecked(id, checked),
      [updateExtraPropertyChecked, updatePropertyChecked]
    );
  const handleAddExtraProperty: NonNullable<QuickFillPropertyPanelState['onAddExtraProperty']> =
    useCallback(
      (property) => addExtraProperty({
        ...property,
        value: '',
        options: [],
      }),
      [addExtraProperty]
    );
  const utilityChromeElement = useMemo(() => (
    <QuickFillUtilityChrome
      pageGuideRef={c.refs.pageGuideRef}
      recoveryControlsRef={c.refs.recoveryControlsRef}
      quickFillUndoTooltip={c.quickFillUndoTooltip}
      hasCompatibleQuickFillSessionUndo={c.compatibleSessionUndoSnapshots.length > 0}
      quickFillSessionUndoDisabled={c.quickFillSessionUndoDisabled}
      isUndoingQuickFillSessions={c.isUndoingQuickFillSessions}
      onUndoQuickFillSessions={() => void handleUndoQuickFillSessions()}
      hasUndoableParameterSetup={c.hasUndoableParameterSetup}
      connectorUndoTooltip={c.connectorUndoTooltip}
      isUndoingParameterSetup={c.parameterAddition.isAdding}
      onUndoParameterSetup={() => void handleUndoParameterSetup()}
      onOpenGuide={c.tour.openGuide}
    />
  ), [
    c.compatibleSessionUndoSnapshots.length, c.connectorUndoTooltip,
    c.hasUndoableParameterSetup, c.isUndoingQuickFillSessions,
    c.parameterAddition.isAdding, c.quickFillSessionUndoDisabled,
    c.quickFillUndoTooltip, c.refs.pageGuideRef, c.refs.recoveryControlsRef,
    c.tour.openGuide, handleUndoParameterSetup, handleUndoQuickFillSessions,
  ]);
  const contextMenu = useMemo(() => (
    <ContextMenu
      open={c.contextMenu.open}
      position={c.contextMenu.position}
      onClose={c.handleContextMenuClose}
      onCut={c.operations.handleCut}
      onCopy={c.handleCopyCurrentSelection}
       onPaste={c.handlePasteCurrentSelection}
      onFillDown={c.handleFillDownClick}
      onFillRight={c.operations.handleFillRight}
      onClearCells={c.handleClearCurrentSelection}
      onClearColumn={c.handleClearActiveColumn}
      onFindReplace={handleFindReplaceOpen}
      onAutoFill={handleAutoFillSelected}
      onSelectInModel={c.handleSelectModelComponents}
      selectedRowCount={selectedRows.length}
      selectedCellCount={selectedCells.length}
      activeColumnLabel={activeColumn}
      fillDownTargetCount={selectedRows.length >= 2
        ? c.fillDownTargetRowIndices.length
        : fillDownCellSelectionCount}
      modelSelectionCount={c.modelSelectionElementIds.length}
      disabledItems={{ selectInModel: !c.canSelectModelComponents }}
    />
  ), [
    activeColumn, c.canSelectModelComponents, c.contextMenu.open,
    c.contextMenu.position, c.fillDownTargetRowIndices.length,
    c.handleClearActiveColumn, c.handleClearCurrentSelection,
    c.handleContextMenuClose, c.handleCopyCurrentSelection, c.handleFillDownClick,
    c.handleSelectModelComponents, c.modelSelectionElementIds.length,
    c.operations.handleCut,
    c.operations.handleFillRight, c.handlePasteCurrentSelection,
    fillDownCellSelectionCount, handleAutoFillSelected, handleFindReplaceOpen,
    selectedCells, selectedRows,
  ]);
  const actionBar = useMemo(() => buildQuickFillActionBarProps({
    totalRows: c.quickFillData.length,
    selectedRowCount: selectedRows.length,
    keyLibraryRowsReady: isQuickFillDatasetModeReady(
      c.quickFillDataRowModes,
      c.effectiveRowMode,
      c.isTableLoading
    ),
    selectedCellCount: selectedCells.length,
    activeColumnLabel: activeColumn,
    fillDownTargetCount: selectedRows.length >= 2
      ? c.fillDownTargetRowIndices.length
      : fillDownCellSelectionCount,
    modelSelectionCount: c.modelSelectionElementIds.length,
    rowMode: c.effectiveRowMode,
    isColumnsPanelOpen: c.propertyPanelOpen,
    modelDataStale: c.modelDataStale,
    refs: {
      autoFillControlsRef: c.refs.autoFillControlsRef,
      manageColumnsRef: c.refs.manageColumnsRef,
      findReplaceRef: c.refs.findReplaceRef,
      rowModeToggleRef: c.refs.rowModeToggleRef,
      modelSelectRef: c.refs.modelSelectRef,
      csvToolsRef: c.refs.csvToolsRef,
      utilityToolsRef: c.refs.utilityToolsRef,
    },
    handlers: {
      onAutoFillAll: handleAutoFillAll,
      onAutoFillSelected: handleAutoFillSelected,
      onAddToKeyLibrary: c.keyLibrary.openFromTable,
      onCopy: c.handleCopyCurrentSelection,
     onPaste: c.handlePasteCurrentSelection,
      onClear: c.handleClearCurrentSelection,
      onClearColumn: c.handleClearActiveColumn,
      onFillDown: c.handleFillDownClick,
      onFindReplace: handleFindReplaceOpen,
      onExport: c.operations.handleCSVExport,
      onImport: handleCSVImportOpen,
      onAddColumns: handleTogglePropertyPanel,
      onRefreshModel: c.handleRefreshModelData,
      onSelectInModel: c.handleSelectModelComponents,
      onToggleRowMode: c.handleToggleRowMode,
    },
  }), [
    activeColumn, c.effectiveRowMode, c.fillDownTargetRowIndices.length,
    c.handleClearActiveColumn, c.handleClearCurrentSelection,
    c.handleCopyCurrentSelection, c.handleFillDownClick, c.handleRefreshModelData,
    c.handleSelectModelComponents,
    c.handleToggleRowMode, c.isTableLoading,
    c.keyLibrary.openFromTable, c.modelDataStale,
    c.modelSelectionElementIds.length,
    c.operations.handleCSVExport,
    c.handlePasteCurrentSelection, c.propertyPanelOpen, c.quickFillData.length,
    c.quickFillDataRowModes, c.refs.autoFillControlsRef, c.refs.csvToolsRef,
    c.refs.findReplaceRef, c.refs.manageColumnsRef, c.refs.modelSelectRef,
    c.refs.rowModeToggleRef, c.refs.utilityToolsRef,
    fillDownCellSelectionCount, handleAutoFillAll,
    handleAutoFillSelected, handleCSVImportOpen, handleFindReplaceOpen,
    handleTogglePropertyPanel, selectedCells,
    selectedRows,
  ]);
  const table = useMemo(() => buildQuickFillTableProps({
    quickFillData: c.quickFillData,
    nameColumnLabel: c.nameColumnLabel,
    selectedRows,
    editedCells: c.editedCells,
    failedCellErrors: c.submit.failedCellErrors,
    validationFocusPreset: c.validationFocusPreset,
    validationFocusColumnKeys: c.validationFocusColumnKeys,
    cellSelection: {
      handleCellClick: c.cellSelection.handleCellClick,
      handleCellMouseDown: c.cellSelection.handleCellMouseDown,
      handleCellMouseEnter: c.cellSelection.handleCellMouseEnter,
      handleCellMouseUp: c.cellSelection.handleCellMouseUp,
      consumePendingCellClick: c.cellSelection.consumePendingCellClick,
      isCellSelected: c.cellSelection.isCellSelected,
    },
    rowSelection: {
      handleSelectRow: c.operations.handleSelectRow,
      handleSelectAllRows: c.operations.handleSelectAllRows,
    },
    viewport: {
      handleFilteredRowIndicesChange: c.navigation.handleFilteredRowIndicesChange,
      handleViewportRangeChange: c.navigation.handleViewportRangeChange,
    },
    tableViewportRef: c.refs.tableViewportRef,
    filterTriggerRef: c.refs.filterTriggerRef,
    filterPopoverRef: c.refs.filterPopoverRef,
    selectedProperties: c.memoizedSelectedProperties,
    onRowChange: c.handleQuickFillRowChange,
    navigationMarkerRows: c.navigation.navigationMarkerRows,
    navigationRequest: c.navigation.navigationRequest,
    activeFindReplaceMatch: c.activeFindReplaceMatch,
    isLoading: c.shouldShowTableLoadingOverlay,
    loadingText: c.activeLoadProgressText ||
      (c.initialization.isInitializing ? 'Fetching data from Revit…'
        : c.builder.isBuilding ? c.builder.buildProgress || 'Building table data…'
          : 'Loading…'),
    previewFilterColumn: c.previewFilterColumn,
    showTourFilterPreview: c.showTourFilterPreview,
  }), [
    c.activeFindReplaceMatch, c.activeLoadProgressText, c.builder.buildProgress,
    c.builder.isBuilding, c.cellSelection.consumePendingCellClick,
    c.cellSelection.handleCellClick, c.cellSelection.handleCellMouseDown,
    c.cellSelection.handleCellMouseEnter, c.cellSelection.handleCellMouseUp,
    c.cellSelection.isCellSelected, c.editedCells, c.handleQuickFillRowChange,
    c.initialization.isInitializing, c.memoizedSelectedProperties,
    c.nameColumnLabel, c.navigation.handleFilteredRowIndicesChange,
    c.navigation.handleViewportRangeChange, c.navigation.navigationMarkerRows,
    c.navigation.navigationRequest, c.operations.handleSelectAllRows,
    c.operations.handleSelectRow, c.previewFilterColumn, c.quickFillData,
    c.refs.filterPopoverRef, c.refs.filterTriggerRef, c.refs.tableViewportRef,
    c.shouldShowTableLoadingOverlay, c.showTourFilterPreview,
    c.submit.failedCellErrors, c.validationFocusColumnKeys,
    c.validationFocusPreset, selectedRows,
  ]);
  const loadingPlaceholder = useMemo(() => ({
    isInitializing: c.initialization.isInitializing,
    isBuilding: c.builder.isBuilding,
    buildProgress: c.builder.buildProgress,
    loadProgress: c.activeLoadProgress,
  }), [
    c.activeLoadProgress, c.builder.buildProgress, c.builder.isBuilding,
    c.initialization.isInitializing,
  ]);
  const navigationDock = useMemo(() => buildQuickFillNavigationDockProps({
    quickFillData: c.quickFillData,
    filteredRowCount: c.navigation.filteredRowIndices.length,
    navigatorRowCoverage: c.navigation.navigatorRowCoverage,
    collapsed: c.navigationPreviewState.collapsed,
    setNavigationDockCollapsed: c.navigation.setNavigationDockCollapsed,
    collapseLocked: c.shouldForceOpenNavigator,
    activeNavigationRowLabel: c.navigationPreviewState.activeRowLabel,
    activeNavigationPosition: c.navigationPreviewState.currentPosition,
    activeRowMarked: c.navigationPreviewState.activeRowMarked,
    canJumpToActive: c.navigationPreviewState.canJumpToActive,
    canToggleMarker: c.navigationPreviewState.canToggleMarker,
    hiddenMarkerCount: c.navigationPreviewState.hiddenMarkerCount,
    markers: c.navigationPreviewState.markers,
    viewportRange: c.navigation.viewportRange,
    handlers: {
      handleJumpToPosition: c.navigation.handleJumpToPosition,
      handleToggleActiveMarker: c.handleToggleMarkerForDock,
      handleRemoveMarker: c.handleRemoveMarkerForDock,
      handleJumpToActiveLocation: c.handleJumpToActiveForDock,
    },
    refs: { navigationDockRef: c.refs.navigationDockRef },
    visibility: {
      isValidationScopedView: c.isValidationScopedView,
      propertyPanelOpen: c.propertyPanelOpen,
      shouldShowValuePanel: c.shouldShowValuePanel,
      useInlineTourDrawers: c.useDrawerPanels && c.isShortViewport,
    },
  }), [
    c.handleJumpToActiveForDock, c.handleRemoveMarkerForDock,
    c.handleToggleMarkerForDock, c.isShortViewport, c.isValidationScopedView,
    c.navigation.filteredRowIndices.length, c.navigation.handleJumpToPosition,
    c.navigation.navigatorRowCoverage, c.navigation.setNavigationDockCollapsed,
    c.navigation.viewportRange, c.navigationPreviewState.activeRowLabel,
    c.navigationPreviewState.activeRowMarked, c.navigationPreviewState.canJumpToActive,
    c.navigationPreviewState.canToggleMarker, c.navigationPreviewState.collapsed,
    c.navigationPreviewState.currentPosition, c.navigationPreviewState.hiddenMarkerCount,
    c.navigationPreviewState.markers, c.propertyPanelOpen, c.quickFillData,
    c.refs.navigationDockRef, c.shouldForceOpenNavigator,
    c.shouldShowValuePanel, c.useDrawerPanels,
  ]);
  const sidePanels = useMemo(() => buildQuickFillSidePanelsProps({
    useDrawerPanels: c.useDrawerPanels,
    valuePanel: {
      shouldShow: c.shouldShowValuePanel,
      showInlineTourPanel: c.showInlineTourValuePanel,
      panelRef: c.refs.valuePanelRef,
      columnLabel: c.valuePanelColumn ?? null,
      options: c.valuePanelColumn
        ? getOptionsForProperty(c.valuePanelColumn, c.valuePanelOptionRowIndices)
        : [],
      searchInputRef: c.refs.valueSearchRef,
      currentValue: c.previewValuePanelActive
        ? c.valuePanelCurrentValue
        : selectedCells.length === 1 && c.valuePanelColumn
          ? c.quickFillData[selectedCells[0].rowIndex]?.[
            c.valuePanelColumn as keyof QuickFillItem
          ] as string
          : undefined,
      onSelect: c.handleValueSelect,
      onClearSelection: c.handleClearCurrentSelection,
      onClearColumn: c.handleClearActiveColumn,
      onClose: c.handleCloseValueSelector,
      usageStats: c.valuePanelColumn ? getColumnUsageStats(c.valuePanelColumn) : {},
      selectedCount: c.valuePanelSelectedCount,
    },
    propertyPanel: {
      open: c.propertyPanelOpen,
      showInlineTourPanel: c.showInlineTourPropertyPanel,
      panelRef: c.refs.propertyPanelRef,
      properties: c.availableProperties,
      extraProperties: c.availableExtraProperties,
      searchInputRef: c.refs.propertySearchRef,
      sharedParameters: c.initialization.sharedParametersOptions,
      availableParameters: c.initialization.availableParameters,
      workflowMode: c.configSync.workflowMode,
      workflowParameters: c.configSync.selectedWorkflowParameters,
      highlightedLabels: c.highlightedQuickFillColumnLabels,
      onPropertyCheckedChange: handlePropertyCheckedChange,
      onParameterChange: c.updatePropertyRevitParameter,
      onAddExtraProperty: handleAddExtraProperty,
      onRemoveExtraProperty: c.removeExtraProperty,
      onExpandChange: handleTogglePropertyPanel,
    },
  }), [
    c.availableExtraProperties, c.availableProperties,
    c.configSync.selectedWorkflowParameters, c.configSync.workflowMode,
    c.handleClearActiveColumn, c.handleClearCurrentSelection,
    c.handleCloseValueSelector, c.handleValueSelect,
    c.highlightedQuickFillColumnLabels, c.initialization.availableParameters,
    c.initialization.sharedParametersOptions, c.previewValuePanelActive,
    c.propertyPanelOpen, c.quickFillData, c.refs.propertyPanelRef,
    c.refs.propertySearchRef, c.refs.valuePanelRef, c.refs.valueSearchRef,
    c.removeExtraProperty, c.shouldShowValuePanel,
    c.showInlineTourPropertyPanel, c.showInlineTourValuePanel,
    c.updatePropertyRevitParameter, c.useDrawerPanels, c.valuePanelColumn,
    c.valuePanelCurrentValue, c.valuePanelOptionRowIndices,
    c.valuePanelSelectedCount, getColumnUsageStats, getOptionsForProperty,
    handleAddExtraProperty, handlePropertyCheckedChange,
    handleTogglePropertyPanel, selectedCells,
  ]);
  // c.modelStatus itself is rebuilt every render by useQuickFillModelStatus,
  // so the banner memoizes on its leaf fields instead of the wrapper object.
  const modelStatusBanner = useMemo(() => (
    <ModelStatusBanner
      activeModelStatus={c.modelStatus.activeModelStatus}
      isModelWriteBlocked={c.modelStatus.isModelWriteBlocked}
      modelBlockReason={c.modelStatus.modelBlockReason}
      modelStatusDialogDismissed={c.modelStatus.modelStatusDialogDismissed}
      setModelStatusDialogDismissed={c.modelStatus.setModelStatusDialogDismissed}
      refreshActiveModelStatus={c.modelStatus.refreshActiveModelStatus}
    />
  ), [
    c.modelStatus.activeModelStatus, c.modelStatus.isModelWriteBlocked,
    c.modelStatus.modelBlockReason, c.modelStatus.modelStatusDialogDismissed,
    c.modelStatus.refreshActiveModelStatus,
    c.modelStatus.setModelStatusDialogDismissed,
  ]);
  const workspaceProps = useMemo(() => buildQuickFillWorkspaceProps({
    modelStatusBanner,
    contextMenu,
    layout: { useDrawerPanels: c.useDrawerPanels, isShortViewport: c.isShortViewport },
    loading: {
      isTableLoading: c.isTableLoading,
      showQuickFillApplyProgress: c.showQuickFillApplyProgress,
      quickFillApplyProgressMessage: c.applyProgressMessage,
      quickFillApplyProgressDetail: c.applyProgressDetail,
      activeLoadProgressText: c.activeLoadProgressText,
      isInitializing: c.initialization.isInitializing,
      isBuilding: c.builder.isBuilding,
      buildProgress: c.builder.buildProgress,
      activeLoadProgress: c.activeLoadProgress,
    },
    data: {
      quickFillData: c.quickFillData,
      visibleProperties: c.visibleProperties,
      rowMode: c.effectiveRowMode,
      validationScopeInfo: c.validationFocus.validationScopeInfo,
      onDismissValidationScope: c.handleDismissValidationScope,
    },
    refs: {
      actionBarRef: c.refs.actionBarRef,
      tableAreaRef: c.refs.tableAreaRef,
      navigationDockRef: c.refs.navigationDockRef,
    },
    actionBar,
    table,
    loadingPlaceholder,
    navigationDock,
    sidePanels,
    onTableContextMenu: c.handleContextMenu,
  }), [
    actionBar, c.activeLoadProgress, c.activeLoadProgressText,
    c.applyProgressDetail, c.applyProgressMessage, c.builder.buildProgress,
    c.builder.isBuilding, c.effectiveRowMode, c.handleContextMenu,
    c.handleDismissValidationScope, c.initialization.isInitializing,
    c.isShortViewport, c.isTableLoading, c.quickFillData,
    c.refs.actionBarRef, c.refs.navigationDockRef, c.refs.tableAreaRef,
    c.showQuickFillApplyProgress, c.useDrawerPanels,
    c.validationFocus.validationScopeInfo, c.visibleProperties, contextMenu,
    loadingPlaceholder, modelStatusBanner, navigationDock, sidePanels, table,
  ]);
  // dialogProps stays un-memoized: it embeds c.keyLibrary.dialogProps, which
  // useQuickFillKeyLibrary rebuilds (with fresh inline closures) on every
  // render, so a memo here could never hit until that hook is stabilized.
  const dialogProps = buildQuickFillDialogShellProps({
    modelStatus: {
      activeModelStatus: c.modelStatus.activeModelStatus,
      isModelWriteBlocked: c.modelStatus.isModelWriteBlocked,
      modelBlockReason: c.modelStatus.modelBlockReason,
      modelStatusDialogDismissed: c.modelStatus.modelStatusDialogDismissed,
      setModelStatusDialogDismissed: c.modelStatus.setModelStatusDialogDismissed,
      refreshActiveModelStatus: c.modelStatus.refreshActiveModelStatus,
    },
    parameterChange: {
      parameterChangeDialog: c.tour.parameterChangeDialog,
      handleCancelParameterChange: c.tour.handleCancelParameterChange,
      handleConfirmParameterChange: c.tour.handleConfirmParameterChange,
    },
    autoFill: {
      effectiveAutoFillDialogOpen: c.tour.effectiveAutoFillDialogOpen,
      autoFillPreviewOnly: c.tour.autoFillPreviewOnly,
      effectiveAutoFillDialogMode: c.tour.effectiveAutoFillDialogMode,
      effectiveAutoFillDialogPreview: c.tour.effectiveAutoFillDialogPreview,
      autoFillPreviewDialogRef: c.refs.autoFillPreviewDialogRef,
      handleCancelAutoFill: c.tour.handleCancelAutoFill,
      handleConfirmAutoFillDialog: c.tour.handleConfirmAutoFillDialog,
    },
    keyLibrary: c.keyLibrary.dialogProps,
    findReplace: {
      effectiveFindReplaceOpen: c.tour.effectiveFindReplaceOpen,
      findReplacePreviewOnly: c.tour.findReplacePreviewOnly,
      findReplaceDialogRef: c.refs.findReplaceDialogRef,
      clearFindReplaceFocus: c.clearFindReplaceFocus,
      setFindReplaceOpen: c.tour.setFindReplaceOpen,
      visibleProperties: c.visibleProperties,
      findReplaceVisibleRowIndices: c.findReplaceVisibleRowIndices,
      selectedRows,
      handleFind: c.operations.handleFind,
      handleReplace: c.operations.handleReplace,
      handleReplaceAll: c.operations.handleReplaceAll,
      handleFindReplaceHighlight: c.handleFindReplaceHighlight,
    },
    csvImport: {
      csvImportOpen: c.tour.csvImportOpen,
      setCSVImportOpen: c.tour.setCSVImportOpen,
      handleCSVPreview: c.operations.handleCSVPreview,
      handleCSVImport: c.operations.handleCSVImport,
    },
    saveReview: {
      effectiveSaveReviewOpen: c.effectiveSaveReviewOpen,
      saveReviewPreviewOnly: c.saveReviewPreviewOnly,
      saveReviewDialogRef: c.refs.saveReviewDialogRef,
      effectiveSaveReviewWarning: c.effectiveSaveReviewWarning,
      effectiveSaveReviewResult: c.effectiveSaveReviewResult,
      successRows: c.successRows,
      failedRows: c.failedRows,
      setSaveResultDialog: c.submit.setSaveResultDialog,
      onContinue,
    },
    navigationGuard: {
      pendingNavigation: c.submit.pendingNavigation,
      isContinuing: c.submit.isContinuing,
      setPendingNavigation: c.submit.setPendingNavigation,
    },
    applyProgress: {
      showQuickFillApplyProgress: c.showQuickFillApplyProgress,
      syncProgress: c.operations.syncProgress,
    },
    guide: {
      guideOpen: c.tour.guideOpen,
      setGuideOpen: c.tour.setGuideOpen,
      handleOpenTour: c.tour.handleOpenTour,
      quickFillGuideContent,
    },
  });

  return { utilityChromeElement, workspaceProps, dialogProps };
}
